import React from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { Button, Modal, NumberInput, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";

import { _CollectionType } from "apollo/graphql.generated";
import useCreatorReserves from "~/features/tokens/sections/ManageToken/modals/CreatorReserves/useCreatorReserves";

interface FormValues {
  numberToMint: 1;
}

const CreatorReservesModal = ({
  collection,
  remainingTokens,
  isOpened,
  onClose,
}: {
  collection: {
    id: string;
    collectionType: _CollectionType;
    editionId?: number | null;
  };
  remainingTokens: number | null;
  isOpened: boolean;
  onClose: () => void;
}) => {
  const form = useForm<FormValues>({
    initialValues: {
      numberToMint: 1,
    },
    validate: (values) => ({
      numberToMint:
        !values.numberToMint ||
        values.numberToMint <= 0 ||
        (remainingTokens != null && values.numberToMint > remainingTokens)
          ? "Invalid range"
          : null,
    }),
  });

  const { buttonLoading, buttonLabel, mint, error } = useCreatorReserves({
    collectionId: collection.id,
    collectionType: collection.collectionType,
    collectionEditionId: collection.editionId || 0,
  });

  return (
    <Modal
      opened={isOpened}
      onClose={onClose}
      size="lg"
      title="Mint creator reserves"
    >
      <form onSubmit={form.onSubmit((values) => mint(values.numberToMint))}>
        <Stack spacing={8}>
          <NumberInput
            disabled={remainingTokens === 0}
            min={1}
            max={remainingTokens != null ? remainingTokens : undefined}
            label="Set the number of tokens to mint"
            {...form.getInputProps("numberToMint")}
          />
          {remainingTokens != null && remainingTokens > 1 && (
            <Text component="span" size="xs" color={TEXT_COLOR.SECONDARY}>
              Mint from 1-{remainingTokens} tokens.
            </Text>
          )}
        </Stack>
        <ModalSection>
          <Button
            fullWidth
            size="lg"
            type="submit"
            loading={buttonLoading}
            disabled={!form.isValid() || remainingTokens === 0}
          >
            {buttonLabel || "Mint"}
          </Button>
          {error && (
            <Text align="center" mt="xs" size="xs" color={STATUS_COLOR.ERROR}>
              {error}
            </Text>
          )}
          <Text size="xs" align="center" mt={8}>
            This action requires a transaction
          </Text>
        </ModalSection>
      </form>
    </Modal>
  );
};

export default CreatorReservesModal;
